/**
 * Unified OTP Service
 * - Nigerian numbers (+234): Uses Termii by default (reliable SMS delivery)
 * - Non-Nigerian numbers: Uses Firebase Phone Auth
 * - Falls back to Firebase for Nigerian numbers if Termii fails
 */

import { termiiApi } from './termiiApi';
import {
  setupRecaptcha,
  sendPhoneOtp,
  verifyPhoneOtp,
  clearPhoneAuthSession,
  hasActivePhoneAuthSession,
  initializeFirebase,
} from './firebase';

// OTP Providers
export const OTP_PROVIDER = {
  TERMII: 'termii',
  FIREBASE: 'firebase',
};

// Store the current provider being used
let currentProvider = null;
let currentPhoneNumber = null;

/**
 * Determine which OTP provider to use based on phone number
 * Nigerian numbers (+234): Use Termii by default
 * Non-Nigerian numbers: Use Firebase
 * @param {string} phoneNumber - Phone number with country code
 * @returns {string} Provider name ('termii' or 'firebase')
 */
export function getOtpProvider(phoneNumber) {
  const isNigerian = isNigerianNumber(phoneNumber);
  const provider = isNigerian ? OTP_PROVIDER.TERMII : OTP_PROVIDER.FIREBASE;

  console.log('[OTP Service] Provider detection:', {
    original: phoneNumber,
    isNigerian,
    provider,
  });

  return provider;
}

/**
 * Check if phone number is Nigerian
 * @param {string} phoneNumber - Phone number with country code
 * @returns {boolean}
 */
export function isNigerianNumber(phoneNumber) {
  return phoneNumber.startsWith('+234') || phoneNumber.startsWith('234');
}

/**
 * Send OTP to phone number
 * - Nigerian numbers (+234): Uses Termii by default (reliable SMS delivery)
 * - Non-Nigerian numbers: Uses Firebase Phone Auth
 * - Falls back to Firebase for Nigerian numbers if Termii fails
 * @param {string} phoneNumber - Phone number with country code
 * @param {string} [recaptchaContainerId] - Container ID for Firebase reCAPTCHA
 * @returns {Promise<{provider: string, success: boolean}>}
 */
export async function sendOtp(phoneNumber, recaptchaContainerId = 'recaptcha-container') {
  console.log('[OTP Service] sendOtp called with:', phoneNumber);

  currentPhoneNumber = phoneNumber;

  // Store phone in session storage
  sessionStorage.setItem('otp_phone', phoneNumber);

  // Nigerian numbers: Use Termii by default (reliable SMS delivery for NG)
  if (isNigerianNumber(phoneNumber)) {
    console.log('[OTP Service] Nigerian number: Using Termii by default');

    try {
      const result = await termiiApi.sendOtp(phoneNumber);

      // Success with Termii
      currentProvider = OTP_PROVIDER.TERMII;
      sessionStorage.setItem('otp_provider', OTP_PROVIDER.TERMII);
      // Store pinId for later retrieval during verification
      if (result.pinId) {
        sessionStorage.setItem('termii_pin_id', result.pinId);
      }

      console.log('[OTP Service] Termii OTP sent successfully');
      return {
        provider: OTP_PROVIDER.TERMII,
        success: true,
        pinId: result.pinId,
      };
    } catch (termiiError) {
      console.error('[OTP Service] Termii failed:', termiiError);

      // Fallback to Firebase for Nigerian numbers if Termii fails
      console.log('[OTP Service] Falling back to Firebase for Nigerian number');
      try {
        await initializeFirebase();
        await setupRecaptcha(recaptchaContainerId);
        const result = await sendPhoneOtp(phoneNumber);

        currentProvider = OTP_PROVIDER.FIREBASE;
        sessionStorage.setItem('otp_provider', OTP_PROVIDER.FIREBASE);
        if (result.verificationId) {
          sessionStorage.setItem('firebase_verification_id', result.verificationId);
        }

        console.log('[OTP Service] Firebase fallback OTP sent successfully');
        return {
          provider: OTP_PROVIDER.FIREBASE,
          success: true,
          verificationId: result.verificationId,
        };
      } catch (firebaseError) {
        console.error('[OTP Service] Firebase fallback also failed:', firebaseError);
        // Throw the original Termii error
        throw termiiError;
      }
    }
  }

  // Non-Nigerian numbers: Use Firebase
  console.log('[OTP Service] Non-Nigerian number: Using Firebase');

  try {
    await initializeFirebase();
    await setupRecaptcha(recaptchaContainerId);
    const result = await sendPhoneOtp(phoneNumber);

    // Success with Firebase
    currentProvider = OTP_PROVIDER.FIREBASE;
    sessionStorage.setItem('otp_provider', OTP_PROVIDER.FIREBASE);
    // Store verificationId for later retrieval during verification
    if (result.verificationId) {
      sessionStorage.setItem('firebase_verification_id', result.verificationId);
    }

    console.log('[OTP Service] Firebase OTP sent successfully');
    return {
      provider: OTP_PROVIDER.FIREBASE,
      success: true,
      verificationId: result.verificationId,
    };
  } catch (firebaseError) {
    console.error('[OTP Service] Firebase failed:', firebaseError);
    // For non-Nigerian numbers, just throw the Firebase error
    throw firebaseError;
  }
}

/**
 * Verify OTP code
 * - For Termii: Returns stored pinId and otp; actual verification happens via /register endpoint
 * - For Firebase: Verifies via Firebase Auth and returns token
 * @param {string} otp - The OTP code entered by user
 * @returns {Promise<{verified: boolean, provider: string, firebaseToken?: string, otpId?: string, otp?: string}>}
 */
export async function verifyOtp(otp) {
  // Get stored provider if not set
  const provider = currentProvider || sessionStorage.getItem('otp_provider');

  if (!provider) {
    throw new Error('No OTP session found. Please request a new code.');
  }

  console.log(`[OTP Service] Verifying OTP via ${provider}`);

  try {
    if (provider === OTP_PROVIDER.TERMII) {
      // For Termii, verification is handled by the backend /register endpoint
      // We just return the stored pinId and otp code
      const storedPinId = termiiApi.getPinId();

      if (!storedPinId) {
        throw new Error('No OTP session found. Please request a new code.');
      }

      console.log('[OTP Service] Termii: Returning pinId for backend verification');
      return {
        verified: true, // Assume valid, backend will verify
        provider,
        otpId: storedPinId, // Termii pinId
        otp: otp, // Pass otp for backend verification
      };
    } else {
      // Get stored verificationId for Firebase
      const storedVerificationId = sessionStorage.getItem('firebase_verification_id');
      const result = await verifyPhoneOtp(otp);

      // Get Firebase ID token for backend verification
      let firebaseToken = null;
      if (result.user) {
        try {
          firebaseToken = await result.user.getIdToken();
          console.log('[OTP Service] Got Firebase ID token');
        } catch (tokenError) {
          console.error('[OTP Service] Failed to get Firebase ID token:', tokenError);
        }
      }

      return {
        verified: result.verified,
        provider,
        user: result.user,
        firebaseToken, // Firebase ID token (phoneVerificationToken)
        otpId: storedVerificationId || result.verificationId, // Firebase verificationId
        otp: otp, // Pass otp for backend verification
      };
    }
  } catch (error) {
    console.error(`[OTP Service] Error verifying OTP via ${provider}:`, error);
    throw error;
  }
}

/**
 * Resend OTP to the same phone number
 * @param {string} [phoneNumber] - Phone number (uses stored if not provided)
 * @param {string} [recaptchaContainerId] - Container ID for Firebase reCAPTCHA
 * @returns {Promise<{provider: string, success: boolean}>}
 */
export async function resendOtp(phoneNumber = null, recaptchaContainerId = 'recaptcha-container') {
  const phone = phoneNumber || currentPhoneNumber || sessionStorage.getItem('otp_phone');

  if (!phone) {
    throw new Error('No phone number found. Please start over.');
  }

  // Clear existing session
  clearSession();

  // Send new OTP
  return sendOtp(phone, recaptchaContainerId);
}

/**
 * Clear OTP session
 */
export function clearSession() {
  // Clear Termii session
  termiiApi.clearSession();

  // Clear Firebase session
  clearPhoneAuthSession();

  // Clear local state
  currentProvider = null;
  currentPhoneNumber = null;
  sessionStorage.removeItem('otp_provider');
  sessionStorage.removeItem('otp_phone');
  sessionStorage.removeItem('firebase_verification_id');
  sessionStorage.removeItem('termii_pin_id');
}

/**
 * Check if there's an active OTP session
 */
export function hasActiveSession() {
  const provider = currentProvider || sessionStorage.getItem('otp_provider');

  if (!provider) return false;

  if (provider === OTP_PROVIDER.TERMII) {
    return termiiApi.hasActiveSession();
  } else {
    return hasActivePhoneAuthSession();
  }
}

/**
 * Get current OTP provider
 */
export function getCurrentProvider() {
  return currentProvider || sessionStorage.getItem('otp_provider');
}

/**
 * Get stored phone number
 */
export function getStoredPhoneNumber() {
  return currentPhoneNumber || sessionStorage.getItem('otp_phone');
}

export const otpService = {
  sendOtp,
  verifyOtp,
  resendOtp,
  clearSession,
  hasActiveSession,
  getOtpProvider,
  isNigerianNumber,
  getCurrentProvider,
  getStoredPhoneNumber,
  OTP_PROVIDER,
};

export default otpService;
